import DateFnsUtils from '@date-io/date-fns';
import { Grid, InputLabel, Select } from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import React, { useCallback, useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import HeaderPage from '~/components/HeaderPage';
import { api } from '~/services/api';
import { ApplicationState } from '~/store';
import { categoriaRequest } from '~/store/ducks/common/actions';
import {
  cleanListaAuditoria,
  getListaAuditoriaRequest,
} from '~/store/ducks/ListaAuditoria/actions';
import getValidationErrors from '~/utils/getValidationErrors';
import {
  Container,
  FormControlLGPD,
  SearchButton,
  StyledButton,
  StyledTextField,
} from './styles';
import Table from './Table';

interface Tes {
  data: Date;
}

const localeMap = {
  ptBR,
};

const ListaAuditoria: React.FC = () => {
  const dispatch = useDispatch();

  const auditoria = useSelector<ApplicationState, any>(
    state => state.listaAuditoria,
  );

  const categorias = useSelector<ApplicationState, any>(
    state => state.common.categorias,
  );

  const { company, token } = useSelector<ApplicationState, any>(
    state => state.auth,
  );

  const [identification_key, setIdentification_key] =
    React.useState<any>(undefined);

  const [page, setPage] = React.useState({ value: 0, rowsPerPage: 5 });

  const [ResetPage, setResetPage] = React.useState<number>(0);

  const [selectedDateStart, setSelectedDateStart] =
    React.useState<Date | null>();

  const [selectedDateEnd, setSelectedDateEnd] = React.useState<Date | null>();

  const handleDateChangeStart = (date: Date | null) => {
    setSelectedDateStart(date);
  };

  const handleDateChangeEnd = (date: Date | null) => {
    setSelectedDateEnd(date);
  };

  const [ownership, setOwnership] = useState<number | undefined>(0);

  const [formErr, setFormErr] = useState<any>({});

  const handleChangeOwnership = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: any;
    }>,
  ) => {
    setOwnership(event.target.value);
    setFormErr({
      ...formErr,
      ownership_type: false,
    });
  };

  const getAuditoria = useCallback(async () => {
    try {
      const data = {
        identification_key,
        ownership_type: ownership,
      };

      const schema = Yup.object().shape({
        identification_key: Yup.string().required('CPF obrigatorio!'),
        ownership_type: Yup.string().test(
          'ownership_type',
          'Categoria obrigatoria',
          value => {
            return value !== '0' && value !== '' ? true : false;
          },
        ),
      });

      await schema.validate(data, { abortEarly: false });

      setResetPage(ResetPage + 1);

      const identificationKeyChanged = identification_key.replace(
        /[^\d]+/g,
        '',
      );

      const AuditoriaSetings = {
        pageNo: 0,
        pageSize: 5,
        identificationKey: identificationKeyChanged,
        ownershipType: ownership as number,
        dtInicio: selectedDateStart
          ? format(selectedDateStart as Date, 'dd/MM/yyy')
          : undefined,
        dtFim: selectedDateEnd
          ? format(selectedDateEnd as Date, 'dd/MM/yyy')
          : undefined,
        company: company?.country_company_id,
      };

      dispatch(getListaAuditoriaRequest(AuditoriaSetings));
    } catch (e) {
      if (e instanceof Yup.ValidationError) {
        const err = getValidationErrors(e);

        toast.error('Necessário o preenchimento dos campos de identificação.');

        setFormErr(err);
      }
    }
  }, [
    identification_key,
    ownership,
    ResetPage,
    selectedDateStart,
    selectedDateEnd,
    company,
    dispatch,
  ]);

  useEffect(() => {
    if (selectedDateStart && selectedDateEnd && identification_key) {
      const identificationKeyChanged = identification_key.replace(
        /[^\d]+/g,
        '',
      );

      const AuditoriaSetings = {
        pageNo: page.value,
        pageSize: page.rowsPerPage,
        identificationKey: identificationKeyChanged,
        ownershipType: ownership as number,
        dtInicio: selectedDateStart
          ? format(selectedDateStart as Date, 'dd/MM/yyy')
          : undefined,
        dtFim: selectedDateEnd
          ? format(selectedDateEnd as Date, 'dd/MM/yyy')
          : undefined,
        company: company?.country_company_id,
      };

      dispatch(getListaAuditoriaRequest(AuditoriaSetings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.rowsPerPage, page.value]);

  const handleChangeIdentificationKey = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIdentification_key(event.target.value);
      setFormErr({
        ...formErr,
        identification_key: false,
      });
    },
    [formErr],
  );

  useEffect(
    () => () => {
      dispatch(cleanListaAuditoria());
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(categoriaRequest());
  }, [dispatch]);

  const generatePdf = useCallback(async () => {
    try {
      if (auditoria.data[0]) {
        const identificationKeyChanged = identification_key.replace(
          /[^\d]+/g,
          '',
        );

        const response = await api.get(
          `/v1/logs?pageNo=${0}&pageSize=${
            auditoria.data[0].total_itens
          }&identificationKey=${identificationKeyChanged}&ownershipType=${ownership}${
            selectedDateStart
              ? '&dtInicio=' + format(selectedDateStart as Date, 'dd/MM/yyy')
              : ''
          }${
            selectedDateEnd
              ? '&dtFim=' + format(selectedDateEnd as Date, 'dd/MM/yyy')
              : ''
          }`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              company: company?.country_company_id,
            },
          },
        );

        const body = response.data.map((el: any) => {
          return [
            format(el.log_dt, 'dd/MM/yyyy hh:mm'),
            el.log_id,
            el.policy_process_ds,
            el.document_id,
          ];
        });

        const doc = new jsPDF();

        //@ts-ignore
        doc.autoTable({
          headStyles: {
            fontSize: 7,
            textColor: '#fff',
            fillColor: '#282c34',
          },
          bodyStyles: {
            fontSize: 7,
          },
          margin: { top: 8, left: 8, right: 8, bottom: 8 },
          head: [
            ['Data / hora', 'ID do evento', 'Descrição do evento', 'Detalhe'],
          ],
          body: [...body],
        });

        doc.save('lista_de_auditoria.pdf');
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, [
    auditoria.data,
    company,
    identification_key,
    ownership,
    selectedDateEnd,
    selectedDateStart,
    token,
  ]);

  return (
    <Container>
      <HeaderPage pageTitle="Lista de auditoria" />

      <div className="main-content-event">
        <h1>Insira os dados do titular</h1>

        <div className="search-event">
          <InputMask
            mask="999.999.999-99"
            disabled={false}
            maskChar=""
            onChange={handleChangeIdentificationKey}
            value={identification_key}
            children={() => (
              <StyledTextField
                identificationKeyError={
                  !!formErr?.identification_key ? true : false
                }
                id="identificationKey"
                label="CPF do titular"
                variant="outlined"
                className="TextField-lgpd"
              />
            )}
          />

          <FormControlLGPD
            variant="outlined"
            ownershipTypeError={!!formErr?.ownership_type ? true : false}
          >
            <InputLabel htmlFor="outlined-ownershipType-native-simple">
              Categoria
            </InputLabel>
            <Select
              native
              value={ownership}
              onChange={handleChangeOwnership}
              label="Categoria"
              inputProps={{
                name: 'ownershipType',
                id: 'ownershipType',
              }}
            >
              <option aria-label="ownershipType" value="" />
              {categorias &&
                categorias.map((el: any) => (
                  <option
                    key={el.data_holder_cat_id}
                    value={el.data_holder_cat_id}
                  >
                    {el.data_holder_cat_name}
                  </option>
                ))}
            </Select>
          </FormControlLGPD>

          <MuiPickersUtilsProvider
            locale={localeMap['ptBR']}
            utils={DateFnsUtils}
          >
            <div className="PickerLGPD">
              <Grid container justify="space-around">
                <DatePicker
                  clearable
                  clearLabel="Limpar"
                  cancelLabel="Cancelar"
                  disableFuture
                  format="dd/MM/yyyy"
                  label="Data início"
                  value={selectedDateStart ? selectedDateStart : null}
                  onChange={handleDateChangeStart}
                  animateYearScrolling
                />

                <DatePicker
                  clearable
                  clearLabel="Limpar"
                  cancelLabel="Cancelar"
                  disableFuture
                  format="dd/MM/yyyy"
                  label="Data fim"
                  value={selectedDateEnd ? selectedDateEnd : null}
                  onChange={handleDateChangeEnd}
                  animateYearScrolling
                />
              </Grid>
            </div>
          </MuiPickersUtilsProvider>

          <SearchButton onClick={getAuditoria}>Pesquisar</SearchButton>
        </div>

        <div className="lista-eventos">
          <h2>Lista de eventos</h2>

          <Table
            ResetPage={ResetPage}
            className="table-lgpd"
            getOrderHandleChangePage={(value: number, rowsPerPage: number) => {
              const identificationKeyChanged = identification_key.replace(
                /[^\d]+/g,
                '',
              );

              const AuditoriaSetings = {
                pageNo: value,
                pageSize: 5,
                identificationKey: identificationKeyChanged,
                ownershipType: ownership as number,
                dtInicio: selectedDateStart
                  ? format(selectedDateStart as Date, 'dd/MM/yyy')
                  : undefined,
                dtFim: selectedDateEnd
                  ? format(selectedDateEnd as Date, 'dd/MM/yyy')
                  : undefined,
                company: company?.country_company_id,
              };

              dispatch(getListaAuditoriaRequest(AuditoriaSetings));
              setPage({ value, rowsPerPage });
            }}
            data={auditoria.data}
          />
        </div>

        <StyledButton className="button-continue" onClick={generatePdf}>
          Exportar
        </StyledButton>
      </div>
    </Container>
  );
};

export default ListaAuditoria;
